import { useState } from 'react';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ErrorCode } from '@siteflow/shared';
import { AuthError } from '@/components/auth/auth-error';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError, messageForError } from '@/lib/api-client';
import { authApi } from '@/lib/api/auth';
import { setLastOrgId, setSession } from '@/lib/auth-store';

const registerSchema = z.object({
  name: z.string().min(1, 'Tell us your name').max(120),
  email: z.email('Enter a valid email address'),
  password: z
    .string()
    .min(10, 'Use at least 10 characters')
    .max(200)
    .refine((value) => /[a-z]/i.test(value), 'Include at least one letter')
    .refine((value) => /[0-9]|[^a-z0-9]/i.test(value), 'Include a number or symbol'),
  organisationName: z.string().min(1, 'Name your organisation').max(200),
});

type RegisterValues = z.infer<typeof registerSchema>;
const FIELDS = ['name', 'email', 'password', 'organisationName'] as const;

function RegisterPage() {
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: { name: '', email: '', password: '', organisationName: '' },
  });

  async function onSubmit(values: RegisterValues) {
    setFormError(null);
    try {
      const result = await authApi.register(values);
      setSession({
        accessToken: result.accessToken,
        user: result.user,
        organisations: result.organisations,
      });

      const target = result.organisations[0];
      if (!target) {
        void navigate({ to: '/onboarding' });
        return;
      }
      setLastOrgId(target.id);
      void navigate({ to: '/app/$orgId', params: { orgId: target.id } });
    } catch (error) {
      if (ApiError.is(error)) {
        if (error.code === ErrorCode.CONFLICT) {
          form.setError('email', { message: 'An account already uses that email address.' });
          return;
        }
        if (error.code === ErrorCode.VALIDATION_FAILED) {
          const fields = error.fieldErrors();
          let matched = false;
          for (const name of FIELDS) {
            const message = fields[name];
            if (message) {
              form.setError(name, { message });
              matched = true;
            }
          }
          if (matched) return;
        }
      }
      setFormError(messageForError(error, 'We could not create your account. Try again.'));
    }
  }

  const pending = form.formState.isSubmitting;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <span className="section-label">Get started</span>
        <h1 className="text-title font-semibold tracking-tight text-ink">Create your workspace</h1>
        <p className="text-data text-ink-muted">
          You will be the owner of this organisation and can invite the rest of the team next.
        </p>
      </header>

      <Form {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <AuthError message={formError} />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Your name</FormLabel>
                <FormControl>
                  <Input autoComplete="name" disabled={pending} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Work email</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="email" disabled={pending} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="new-password" disabled={pending} {...field} />
                </FormControl>
                <FormDescription>At least 10 characters, with a number or symbol.</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="organisationName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Organisation name</FormLabel>
                <FormControl>
                  <Input autoComplete="organization" disabled={pending} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" size="lg" disabled={pending} className="mt-1 w-full">
            {pending ? 'Creating your workspace…' : 'Create account'}
          </Button>
        </form>
      </Form>

      <p className="border-t border-hairline pt-4 text-data text-ink-muted">
        Already have an account?{' '}
        <Link to="/login" className="text-accent hover:underline underline-offset-4">
          Sign in
        </Link>
      </p>
    </div>
  );
}

export const Route = createFileRoute('/(auth)/register')({
  // Editors are added by an administrator; creating an organisation of their own makes no sense here.
  beforeLoad: () => {
    if (CONTENT_ONLY) throw redirect({ to: '/login' });
  },
  component: RegisterPage,
});
